import { Component, effect, inject, OnInit } from '@angular/core';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { Commune, PolarConfiguration } from '../../../models/commune';
import { CheckboxModule } from 'primeng/checkbox';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { RadioButtonModule } from 'primeng/radiobutton';

@Component({
  selector: 'app-configuration-chart-polar',
  standalone: true,
  imports: [
    CheckboxModule,
    ColorsetDropdownComponent,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    HeaderComponent,
    NgIf,
    PaginatorModule,
    RadioButtonModule,
    ReactiveFormsModule,
  ],
  templateUrl: './configuration-chart-polar.component.html',
  styleUrl: './configuration-chart-polar.component.scss',
})
export class ConfigurationChartPolarComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  polarConfiguration?: PolarConfiguration;

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.communeSetup();
    });
  }

  /**
   * Sets up the active commune by configuring atlas configurations and initializing the form.
   * If an active commune is not already established, it retrieves the active commune from the global store.
   * Ensures that atlas configurations are set for the commune, and initializes the polar area configuration.
   * If the commune setup is completed and it has not been already initialized, prepares the form setup.
   *
   * @return {void} This method does not return any value.
   */
  communeSetup(): void {
    if (!this.activeCommune) {
      this.activeCommune = this.globalStore.activeCommune();
      if (!this.activeCommune) return;
      if (!this.activeCommune.atlasConfig) {
        this.activeCommune.atlasConfig = this.communeStore.baseConfig();
      }
      this.polarConfiguration = this.activeCommune.atlasConfig.polarArea;
    }
    if (this.activeCommune && !this.initialized) {
      this.initForm();
    }
  }

  /**
   * Lifecycle hook that is called after the component has been initialized.
   * It initializes necessary setups by invoking the `communeSetup` method.
   *
   * @return {void} No return value.
   */
  ngOnInit(): void {
    this.communeSetup();
  }

  /**
   * Initializes the form with default values and sets up value change subscriptions.
   * Creates a form group using predefined values from the polarConfiguration object
   * and assigns it to the form property. Listens for form value changes and triggers
   * the submit method upon changes. Sets the initialized flag to true.
   *
   * @return {void} This method does not return any value.
   */
  initForm(): void {
    this.form = this.fb.group({
      colorset: [this.polarConfiguration?.colorset],
      displayValues: [this.polarConfiguration?.displayValues || false],
      //sort: [this.polarConfiguration?.sort || 'value-asc'],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });

    this.initialized = true;
  }

  /**
   * Submits the form data for the active commune and updates its configuration.
   * Merges the form values with the existing polar area configuration and updates the commune store.
   * @return {void} No return value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig.polarArea = {
      ...this.activeCommune.atlasConfig.polarArea,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }
}
